import { EventStatus } from "@garther/interfaces";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Image,
  Spinner
} from "@garther/ui/components";
import { cn, useI18n } from "@garther/ui/libs";
import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";
import { Ellipsis, Lock, MapPin, Pencil, Unlock } from "lucide-react";

import { useEventsInvalidation, useUpdateEventStatus } from "@/features/event/hooks/use-events-query";

import { EventInfo } from "./-components/event-info";

export const Route = createFileRoute("/_auth/org/$orgId/event/$eventId/")({
  component: Page
});

function Page() {
  const { org, event } = useRouteContext({
    from: "/_auth/org/$orgId/event/$eventId/"
  });
  const { t } = useI18n();
  const navigate = useNavigate();

  const { invalidateList } = useEventsInvalidation();
  const { mutate: updateEventStatus, isPending: isLoading } = useUpdateEventStatus({
    onSuccess: () => invalidateList()
  });

  const updateStatus = (status: EventStatus) => {
    updateEventStatus({ eventId: event.id, data: { status } });
  };

  return (
    <div className="relative min-h-full">
      <div className="relative w-full aspect-[21/9] max-h-[400px]">
        <Image
          src={event.banner || event.featuredImages[0]}
          alt={event.title}
          className="w-full h-full object-cover rounded-2xl"
        />
      </div>

      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-responsive-2xl font-medium text-text-primary">{event.title}</h1>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Ellipsis className="h-9 w-9 text-text-primary p-2 hover:bg-background-highlight border border-border hover:border-border-dark cursor-pointer rounded-md" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="right">
              <DropdownMenuItem
                icon={<Pencil size={18} strokeWidth={2} />}
                onClick={() =>
                  navigate({
                    to: "/org/$orgId/event/$eventId/edit",
                    params: { orgId: org.id, eventId: event.id }
                  })
                }
              >
                {t("common.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={
                  isLoading ? (
                    <Spinner
                      className={cn(
                        "w-[18px] h-[18px]",
                        event.status === EventStatus.AVAILABLE ? "text-red-100 fill-red-500" : ""
                      )}
                    />
                  ) : event.status === EventStatus.AVAILABLE ? (
                    <Lock size={18} strokeWidth={2} />
                  ) : (
                    <Unlock size={18} strokeWidth={2} />
                  )
                }
                variant={event.status === EventStatus.AVAILABLE ? "danger" : "default"}
                onClick={() =>
                  updateStatus(
                    event.status === EventStatus.AVAILABLE ? EventStatus.LOCKED : EventStatus.AVAILABLE
                  )
                }
              >
                {event.status === EventStatus.AVAILABLE ? "Lock event" : "Unlock event"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex flex-wrap gap-4 text-responsive-base">
          <div className="flex items-center gap-2 text-text-secondary">
            <MapPin className="h-5 w-5" />
            <span>{event.location?.address || "HCM, Vietnam"}</span>
          </div>
        </div>

        <EventInfo event={event} org={org} />
      </div>
    </div>
  );
}
